import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Loader2, Mail, MapPin } from 'lucide-react';
import Reveal from './Reveal';
import { CONTACT_INFO } from '../lib/site';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', email: '', company: '', subject: '', message: '' };

const inputClass =
  'w-full bg-transparent border-b border-white/15 text-white placeholder-[#64748B] font-body text-base py-3 focus:outline-none focus:border-[#00E5FF] transition-colors duration-300 font-light';

const labelClass =
  'font-body text-[11px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium block mb-2';

const Field = ({ id, label, value, onChange, placeholder, type = 'text', autoComplete, testId }) => (
  <div>
    <label htmlFor={id} className={labelClass}>{label}</label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      data-testid={testId}
      className={inputClass}
    />
  </div>
);

const ContactInfo = () => (
  <div>
    <Reveal>
      <span className="font-body text-[11px] font-semibold uppercase tracking-[0.25em] text-[#00E5FF]">
        Contact Us
      </span>
      <span className="line-electric mt-3 mb-6 block" />
      <h2
        className="font-display font-semibold text-white leading-[1.1]"
        style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)' }}
        data-testid="contact-headline"
      >
        Let&rsquo;s build the next trade together.
      </h2>
      <p className="mt-6 font-body text-base text-[#94A3B8] leading-relaxed font-light">
        {CONTACT_INFO.intro}
      </p>
    </Reveal>

    <Reveal delay={0.1} className="mt-12 space-y-6">
      <a
        href={`mailto:${CONTACT_INFO.email}`}
        className="flex items-center gap-5 group"
        data-testid="contact-email-link"
      >
        <span className="flex h-11 w-11 flex-none items-center justify-center border border-white/10 group-hover:border-[#00E5FF]/60 text-[#00E5FF] transition-colors duration-300">
          <Mail size={16} />
        </span>
        <span className="font-body text-[#94A3B8] group-hover:text-white transition-colors duration-300 text-sm tracking-wide">
          {CONTACT_INFO.email}
        </span>
      </a>
      <div className="flex items-center gap-5">
        <span className="flex h-11 w-11 flex-none items-center justify-center border border-white/10 text-[#00E5FF]">
          <MapPin size={16} />
        </span>
        <span className="font-body text-[#94A3B8] text-sm tracking-wide whitespace-pre-line">{CONTACT_INFO.location}</span>
      </div>
    </Reveal>
  </div>
);

const SubmitButton = ({ loading }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid="contact-submit-button"
    className="group inline-flex items-center gap-2 font-body font-semibold text-[12px] text-[#050B14] bg-[#00E5FF] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 px-8 py-4 uppercase tracking-widest"
  >
    {loading ? (
      <>Sending <Loader2 size={15} className="animate-spin" /></>
    ) : (
      <>Send Message <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" /></>
    )}
  </button>
);

const buildPayload = (form) => ({
  name: form.name.trim(),
  email: form.email.trim(),
  company: form.company.trim() || null,
  subject: form.subject.trim() || null,
  message: form.message.trim(),
});

const useContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please add your name, email and a message.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, buildPayload(form));
      toast.success('Message received. Our team will be in touch shortly.');
      setForm(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return { form, loading, update, submit };
};

const ContactForm = ({ form, loading, update, submit }) => (
  <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
      <Field id="c-name" label="Full Name *" value={form.name} onChange={update('name')} placeholder="Jane Doe" autoComplete="name" testId="contact-input-name" />
      <Field id="c-email" label="Email *" type="email" value={form.email} onChange={update('email')} placeholder="jane@company.com" autoComplete="email" testId="contact-input-email" />
      <Field id="c-company" label="Company" value={form.company} onChange={update('company')} placeholder="Company name" autoComplete="organization" testId="contact-input-company" />
      <Field id="c-subject" label="Subject" value={form.subject} onChange={update('subject')} placeholder="How can we help?" testId="contact-input-subject" />
    </div>

    <div>
      <label htmlFor="c-message" className={labelClass}>Message *</label>
      <textarea
        id="c-message"
        rows={5}
        value={form.message}
        onChange={update('message')}
        placeholder="Tell us about your requirements..."
        data-testid="contact-input-message"
        className={`${inputClass} resize-none`}
      />
    </div>

    <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
      <p className="font-body text-xs text-[#94A3B8] uppercase tracking-wider">
        Typically respond within one business day.
      </p>
      <SubmitButton loading={loading} />
    </div>
  </form>
);

export default function Contact() {
  const { form, loading, update, submit } = useContactForm();

  return (
    <section id="contact" className="bg-[#050B14] py-28 lg:py-36 relative overflow-hidden" data-testid="contact-section">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00E5FF]/3 rounded-full blur-[150px] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <ContactInfo />

        <Reveal delay={0.08}>
          <ContactForm form={form} loading={loading} update={update} submit={submit} />
        </Reveal>
      </div>
    </section>
  );
}
